import { useCallback, useEffect, useState, type KeyboardEvent, type ReactElement } from "react";
import { Pencil } from "lucide-react";
import { space } from "@tokens/index";
import { Button } from "@primitives/Button";
import { IconButton } from "@primitives/IconButton";
import { Stack } from "@primitives/Stack";
import { Text } from "@primitives/Text";
import { TextInput } from "@primitives/TextInput";
import { useViewport } from "@primitives/viewport";

/**
 * The queue name as an `h1` with an inline rename. `compact` stacks the
 * input over the actions; `medium`+ keeps them on one line.
 */

export interface NameEditorProps {
    readonly currentName: string;
    readonly canEdit: boolean;
    readonly isSaving: boolean;
    readonly error: Error | null;
    readonly onSave: (name: string) => void;
}

export function NameEditor({
    currentName,
    canEdit,
    isSaving,
    error,
    onSave,
}: NameEditorProps): ReactElement {
    const { isCompact } = useViewport();
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(currentName);

    useEffect(() => {
        if (!isEditing) setDraft(currentName);
    }, [currentName, isEditing]);

    const startEdit = useCallback(() => {
        setDraft(currentName);
        setIsEditing(true);
    }, [currentName]);

    const cancelEdit = useCallback(() => {
        setDraft(currentName);
        setIsEditing(false);
    }, [currentName]);

    const commit = useCallback(() => {
        const trimmed = draft.trim();
        if (trimmed.length === 0 || trimmed === currentName) {
            cancelEdit();
            return;
        }
        onSave(trimmed);
        setIsEditing(false);
    }, [draft, currentName, onSave, cancelEdit]);

    const handleKey = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                commit();
            } else if (e.key === "Escape") {
                e.preventDefault();
                cancelEdit();
            }
        },
        [commit, cancelEdit],
    );

    if (!isEditing) {
        return (
            <Stack direction="column" gap={1}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: space[2], minWidth: 0 }}>
                    <Text as="h1" role="display" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {currentName}
                    </Text>
                    {canEdit && (
                        <IconButton
                            icon={Pencil}
                            aria-label="Rename queue"
                            tooltip="Rename queue"
                            onClick={startEdit}
                        />
                    )}
                </div>
                {error !== null && (
                    <Text role="caption" tone="danger">
                        {error.message}
                    </Text>
                )}
            </Stack>
        );
    }

    return (
        <div
            style={{
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                alignItems: isCompact ? "stretch" : "center",
                gap: space[2],
                width: "100%",
                minWidth: 0,
            }}
        >
            <TextInput
                autoFocus
                fullWidth
                value={draft}
                disabled={isSaving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                aria-label="Queue name"
            />
            <div style={{ display: isCompact ? "grid" : "flex", gap: space[2], flexShrink: 0 }}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={commit}
                    disabled={isSaving || draft.trim().length === 0}
                >
                    Save
                </Button>
                <Button variant="ghost" size="md" onClick={cancelEdit} disabled={isSaving}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}
